import type { Location as HistoryLocation } from 'history'
import { Base64 } from 'js-base64'
import { parse, stringify } from 'query-string'
import React, { useContext, useEffect, useMemo, useRef } from 'react'
import { matchPath, useHistory, useLocation } from 'react-router'
import { translateSearchQueryParamsToNewApi } from '../../../utils'
import { FeaturesEnum } from '../../../utils/features'
import { addLeadingSlash } from '../../../utils/urlHelpers'
import CustomPlanCTA from '../../components/CustomPlan'
import { BLANK_SEARCH_QUERY_PARAMS } from '../../constants'
import { useMemoizedFn } from '../../hooks/useMemoizedFn'
import { MLProvider, UserContext, UserPreferencesProvider } from '../../providers'
import { PublicLinksInfoContext } from '../../providers/PublicLinksInfoProvider'
import { CustomMetadataContext } from '../../providers/CustomMetadataProvider'

interface BridgeProps {
  sourceLocation: HistoryLocation // the switchLocation from the router (background)
}

export const MediaLibraryProviderBridge: React.FC<BridgeProps> = ({
  children,
  sourceLocation,
}) => {
  const liveLocation = useLocation() // real location for pushing updates
  const history = useHistory()
  const { isPublicLink } = useContext(PublicLinksInfoContext)
  const { user } = useContext(UserContext)
  const { cmFields: customMetadataFields } = useContext(CustomMetadataContext)
  const [readyToRender, setReadyToRender] = React.useState(false)
  const coldBootRef = React.useRef(true)

  // ---- Route parameter extraction (from BACKGROUND location)
  const pathname = sourceLocation.pathname

  const collectionsMatch = matchPath<{
    mediaCollectionId: string
    folderPathBase64?: string
  }>(pathname, {
    path: [
      '/media-collections/:mediaCollectionId/:folderPathBase64?',
      '/media-collections/:mediaCollectionId',
      '/media-collections',
    ],
    exact: true,
  })

  const libraryMatch = matchPath<{ folderPathBase64?: string }>(pathname, {
    path: ['/media-library/:folderPathBase64?'],
    exact: true,
  })

  const route = useMemo(() => {
    if (sourceLocation.pathname.startsWith('/media-collections')) {
      return 'media-collections'
    }
    if (sourceLocation.pathname === '/search') {
      return 'search'
    }
    if (sourceLocation.pathname.startsWith('/media-library')) {
      return 'media-library'
    }
    return 'media-library' // default fallback
  }, [sourceLocation.pathname])

  const mediaCollectionId = collectionsMatch?.params.mediaCollectionId
  const folderPathBase64 =
    collectionsMatch?.params.folderPathBase64 ??
    libraryMatch?.params.folderPathBase64 ??
    'Lw==' // Base64 for "/"

  const isDetail = Boolean(
    matchPath(liveLocation.pathname, {
      path: [
        '/media-library/detail/:fileId',
        '/media-library/detail/:fileId/:versionId',
        '/media-collections/:mediaCollectionId/detail/:fileId',
        '/media-collections/:mediaCollectionId/detail/:fileId/:versionId',
      ],
      exact: true,
    }),
  )
  // direct detail route: real URL is a detail page and NOT opened as an overlay
  const directDetailRoute = useMemo(() => {
    const isBackground = Boolean(liveLocation.state?.background)
    const isDetailRoute = Boolean(
      matchPath(liveLocation.pathname, {
        path: [
          '/media-library/detail/:fileId',
          '/media-library/detail/:fileId/:versionId',
          '/media-collections/:mediaCollectionId/detail/:fileId',
          '/media-collections/:mediaCollectionId/detail/:fileId/:versionId',
        ],
        exact: true,
      }),
    )
    return isDetailRoute && !isBackground
  }, [liveLocation.pathname, liveLocation.state])

  // If we cold-booted directly into a detail route and the browser
  // restored a background in history.state, drop it so we treat this
  // as a direct detail visit.
  useEffect(() => {
    if (
      coldBootRef.current && // first mount (hard refresh/new load)
      isDetail && // we are on a detail route
      liveLocation.state?.background // browser restored a background
    ) {
      history.replace({
        pathname: liveLocation.pathname,
        search: liveLocation.search,
        hash: liveLocation.hash,
        state: undefined, // <-- clear background
      })
    }
    coldBootRef.current = false
  }, [])

  const folderPath = useMemo(() => {
    try {
      return Base64.decode(folderPathBase64)
    } catch {
      if ((window.env ?? 'dev') === 'dev') {
        console.error(
          '[INVALID_FOLDER_PATH]',
          'Folder path provided is invalid base64',
          folderPathBase64,
        )
      }
      return '/'
    }
  }, [folderPathBase64])

  // ---- QUERY PARAMS (from BACKGROUND search)
  const queryFromUrl = useMemo(
    () => parse(sourceLocation.search),
    [sourceLocation],
  )

  const initialStateRef = useRef(BLANK_SEARCH_QUERY_PARAMS)

  useEffect(() => {
    // translate old API search query params to new API params and redirect to "/search"
    const hasOldParams =
      queryFromUrl.nameValue ||
      queryFromUrl.fileType ||
      queryFromUrl.type ||
      queryFromUrl.searchQuery ||
      queryFromUrl.searchQueryJson ||
      queryFromUrl.semantic ||
      queryFromUrl.semanticUrl ||
      queryFromUrl.isFolderRestrictedSearch ||
      // to redirect to /search if q is present
      queryFromUrl.q

    if (
      addLeadingSlash(sourceLocation.pathname) === '/search' &&
      (Object.keys(queryFromUrl).length === 0 ||
        (Object.keys(queryFromUrl).length === 1 &&
          Object.keys(queryFromUrl).includes('sort')))
    ) {
      history.replace({
        hash: sourceLocation.hash,
        pathname: '/media-library',
        search: sourceLocation.search,
      })
      setReadyToRender(true)
    }
    // translate old API search query params to new API params and redirect to "/search"
    else if (
      !isPublicLink &&
      addLeadingSlash(sourceLocation.pathname) !== '/search' &&
      hasOldParams
    ) {
      const translatedQueryParams = translateSearchQueryParamsToNewApi({
        queryParams: queryFromUrl,
        customMetadataFields,
      })

      const _changedQueryParams = Object.entries(translatedQueryParams).reduce<
        Record<string, any>
      >((acc, [key, value]) => {
        if (initialStateRef.current[key] !== value) {
          acc[key] = value
        }
        return acc
      }, {})

      history.replace({
        hash: sourceLocation.hash,
        pathname: '/search',
        search: stringify(_changedQueryParams, { skipNull: true }),
      })
      setReadyToRender(true)
    } else {
      setReadyToRender(true)
    }
  }, [
    isPublicLink,
    sourceLocation.pathname,
    sourceLocation.hash,
    queryFromUrl,
    folderPath,
    history,
  ])

  const queryParams = useMemo(
    () => ({ ...initialStateRef.current, ...queryFromUrl }),
    [queryFromUrl],
  )

  // ---- setQueryParams (push to REAL location so overlays update the address bar)
  const setQueryParams = useMemoizedFn(newQuery => {
    const finalQueryParams = Object.entries({
      ...queryFromUrl,
      ...newQuery,
    }).reduce<Record<string, any>>((acc, [k, v]) => {
      if (initialStateRef.current[k] !== v) {
        acc[k] = v
      }
      return acc
    }, {})

    const nextSearch = stringify(finalQueryParams, { skipNull: true })
    const currSearch = sourceLocation.search.replace(/^\?/, '')

    if (nextSearch === currSearch) {
      return
    }

    // if previous history state is search, replace the history state with the new search
    if (sourceLocation.pathname === '/search') {
      history.replace({
        pathname: sourceLocation.pathname,
        hash: sourceLocation.hash,
        search: nextSearch,
      })
    } else {
      history.push({
        pathname: sourceLocation.pathname,
        hash: sourceLocation.hash,
        search: nextSearch,
      })
    }
  })

  // ---- Plan gating (same behavior as before)
  const hasStorage =
    isPublicLink ||
    Boolean(
      user?.pricingPlan.featuresList.includes(
        FeaturesEnum.MEDIA_LIBRARY_STORAGE,
      ),
    )

  if (!hasStorage) {
    return <CustomPlanCTA feature={FeaturesEnum.MEDIA_LIBRARY_STORAGE} />
  }

  if (folderPath === null || !readyToRender) {
    return null
  }

  return (
    <UserPreferencesProvider userId={user?.id}>
      <MLProvider
        folderPath={folderPath}
        mediaCollectionId={mediaCollectionId}
        queryParams={queryParams as any}
        setQueryParams={setQueryParams}
        route={route}
        directDetailRoute={directDetailRoute}
      >
        {children}
      </MLProvider>
    </UserPreferencesProvider>
  )
}
